import { Component, EventEmitter, Output } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { VacationService } from '../../../../../../features/vacation/services/vacation.service';
import { ActivatedRoute, Params, Router } from '@angular/router';
// import { HolidayCalendarService } from '../../../../../../features/holiday-calendar/services/holiday-calendar.service';
import { ProjectService } from '../../../../../../features/projects/services/projects.service';
import { VacationHistoryComponent } from '../vacation-history/vacation-history.component';
import { EmployeeService } from '../../../../../../features/employees/employee.service';

@Component({
  selector: 'app-vacation-apply',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatDatepickerModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule,
    VacationHistoryComponent,
  ],
  templateUrl: './vacation-apply.component.html',
  styleUrl: '../../../../employee-details.component.scss',
})
export class VacationApplyComponent {
  vacationApplyForm: FormGroup;
  employeeId: any;
  holidays: any;
  employees: any;
  today: Date = new Date();
  filter: any;
  vacationHistory: any;
  leavePeriod: any;
  leaveBalance: any;
  availableLeave: any;
  @Output() cancelEvent = new EventEmitter<void>();
  passportExpiryDate: any;
  visaExpiryDate: any;
  isLoading:boolean = false;
  leavePeriodStart: any;
  leavePeriodEnd: any;
  dueDate: any;

  constructor(
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private route: ActivatedRoute,
    private router: Router,
    private vacationService: VacationService,
    private toastr: ToastrService,
    // private holidayService: HolidayCalendarService, 
    private projectService: ProjectService,
    private employeeService: EmployeeService
  ) {
    this.route.params.subscribe((params: Params) => {
      this.employeeId = params['id'];
      if (this.employeeId) {
        this.loadEmployeeData(this.employeeId);
        this.fetchVacationsById(this.employeeId, {});
        this.getLeaveBalanceByEmployee(this.employeeId);
      }
    });
  }

  ngOnInit() {
    this.initForm();
    this.loadEmployeeTypeData();

    //employee id
    this.vacationApplyForm.get('employeeId')?.valueChanges.subscribe((id) => {
      this.loadEmployeeData(id);
      this.getLeaveBalanceByEmployee(id);
    });

    //from date
    this.vacationApplyForm
      .get('startDate')
      ?.valueChanges.subscribe((startDate) => {
        const duration = this.vacationApplyForm.get('duration')?.value;
        const endDate = this.vacationApplyForm.get('endDate')?.value;

        this.updateLastWorkingDate(startDate);
        if (duration === '7' || duration === '14' || duration === '21') {
          this.updateEndDateBasedOnDuration(startDate, parseInt(duration, 10));
        }
        if (startDate && endDate) {
          const totalDays = this.calculateTotalDays(startDate, endDate);
          this.availableLeave = this.leaveBalance - totalDays;
        }
      });

    //duration
    this.vacationApplyForm
      .get('duration')
      ?.valueChanges.subscribe((duration) => {
        const startDate = this.vacationApplyForm.get('startDate')?.value;

        if (duration === '7' || duration === '14' || duration === '21') {
          this.updateEndDateBasedOnDuration(startDate, parseInt(duration, 10));
        } else if (duration === 'Custom') {
          this.vacationApplyForm.get('endDate')?.enable();
        }
      });

    //to date
    this.vacationApplyForm.get('endDate')?.valueChanges.subscribe((endDate) => {
      console.log('endDate', endDate);

      const duration = this.vacationApplyForm.get('duration')?.value;
      const startDate = this.vacationApplyForm.get('startDate')?.value;
      this.updateDueBackDate(endDate);

      if (startDate && endDate) {
        const totalDays = this.calculateTotalDays(startDate, new Date(endDate));
        console.log('endDate change', totalDays);
        this.availableLeave = this.leaveBalance - totalDays;
      }

      if (
        !startDate ||
        (duration !== '7' && duration !== '14' && duration !== '21')
      ) {
        return;
      }

      const calculatedEndDate = this.calculateEndDate(
        startDate,
        parseInt(duration, 10)
      );

      if (new Date(endDate) > calculatedEndDate) {
        this.toastr.error(`The end date exceeds the ${duration}-day duration.`);
        this.vacationApplyForm.patchValue({ endDate: calculatedEndDate });
      }
    });

    this.vacationApplyForm
      .get('lastWorkingDate')
      ?.valueChanges.subscribe((lastDate) => {
        const startDate = this.vacationApplyForm.get('startDate')?.value;

        if (lastDate > startDate) {
          this.toastr.error(
            `The last working date cannot be later than the from date.`
          );
          return;
        }
      });
  }

  initForm() {
    const date = new Date();
    const lastWorkingDate = new Date(date);
    const dueBackDate = new Date(date);
    lastWorkingDate.setDate(date.getDate() - 1);
    dueBackDate.setDate(date.getDate() + 1);

    this.vacationApplyForm = this.fb.group({
      employeeId: [this.employeeId || '', Validators.required],
      startDate: [date, Validators.required],
      endDate: [{ value: '', disabled: true }, Validators.required],
      lastWorkingDate: [{ value: lastWorkingDate }],
      dueBackDate: [{ value: dueBackDate }],
      travelDate: [''],
      isTicketEligible: [false],
      isSpouseTicket: [false],
      travelDateOfSpouse: [''],
      travelToOfSpouse: [''],
      travelFromOfSpouse: [''],
      travelFrom: [''],
      travelTo: [''],
      requestDate: [''],
      vacationStatus: [''],
      status: [''],
      duration: ['', Validators.required],
      notes: [''],
    });
  }

  calculateTotalDays(startDate: string | Date, endDate: string | Date): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = end.getTime() - start.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 to include both start & end dates
    return diffDays > 0 ? diffDays : 0;
  }

  fetchHolidays() {
    // this.holidayService.getHolidayCalendarList().subscribe({
    //   next: (data) => {
    //     this.holidays = data.map(
    //       (holiday: any) => new Date(holiday.holidayDate)
    //     );
    //   },
    //   error: (error) => {
    //     console.error(error, 'Error');
    //   },
    // });
  }

  fetchVacationsById(id: any, filter: any) {
    this.vacationService
      .employeeVacationRequests(Number(id), filter)
      .subscribe({
        next: (data) => {
          this.vacationHistory = data;
        },
        error: (error: any) => {
          this.toastr.error(error?.message);
        },
      });
  }

  loadEmployeeData(employeeId: number): void {
    if (employeeId) {
      this.employeeService.getEmployeeById(Number(employeeId)).subscribe({
        next: (data: any) => {
          console.log('data', data);

          const joiningTimestamp = Number(data?.dateOfJoining);
          const employeeJoiningDate = new Date(joiningTimestamp);
          console.log('employeeJoiningDate', employeeJoiningDate);

          if (!isNaN(employeeJoiningDate.getTime())) {
            const month = employeeJoiningDate.getMonth(); // 0-indexed
            const date = employeeJoiningDate.getDate();

            // Current year
            const currentYear = new Date().getFullYear();

            const startDate = new Date(currentYear - 1, month, date);
            const endDate = new Date(currentYear, month, date);

            // Format DD-MM-YYYY
            const formatDate = (d: Date): string => {
              const day = String(d.getDate()).padStart(2, '0');
              const month = String(d.getMonth() + 1).padStart(2, '0');
              const year = d.getFullYear();
              return `${day}-${month}-${year}`;
            };

            const leavePeriodStartDate = formatDate(startDate);
            const leavePeriodEndDate = formatDate(endDate);

            const filter = {
              dateRange: {
                start: leavePeriodStartDate,
                end: leavePeriodEndDate,
              },
            };
            if (leavePeriodStartDate && leavePeriodEndDate) {
              this.leavePeriod =
                leavePeriodStartDate + ' to ' + leavePeriodEndDate;
              this.fetchVacationsById(Number(employeeId), filter);
            }
          } else {
            this.toastr.error('Invalid joining date format', 'Error');
          }

          this.passportExpiryDate = this.datePipe.transform(
            data?.passportExpiryDate,
            'YYYY-MM-dd'
          );
          this.visaExpiryDate = this.datePipe.transform(
            data?.visaExpiryDate,
            'YYYY-MM-dd'
          );
          console.log('this.passportExpiryDate', this.passportExpiryDate);
          console.log('this.visaExpiryDate', this.visaExpiryDate);
          if (data?.employeeData.length > 0) {
            this.leavePeriodStart = data?.employeeData[0]?.leavePeriodStart;
            this.leavePeriodEnd = data?.employeeData[0]?.leavePeriodEnd;
            this.dueDate = data?.employeeData[0]?.dueDate;
          }
        },
        error: (error: any) => {
          this.toastr.error(error, 'Error');
        },
      });
    }
  }

  loadEmployeeTypeData() {
    this.projectService.getAllEmployees('').subscribe({
      next: (employeeTypes) => {
        this.employees = employeeTypes;
      },
      error: (error) => console.error('Error fetching employee types:', error),
    });
  }

  getLeaveBalanceByEmployee(id: any) {
    this.vacationService.getLeaveBalanceByEmployee(Number(id)).subscribe({
      next: (balance) => {
        this.leaveBalance = balance?.leaveCount;
      },
      error: (error) => console.error('Error fetching balance:', error),
    });
  }

  updateEndDateBasedOnDuration(startDate: string, duration: number): void {
    const calculatedEndDate = this.calculateEndDate(startDate, duration);
    this.vacationApplyForm.patchValue({ endDate: calculatedEndDate });
    this.vacationApplyForm.get('endDate')?.disable();
  }

  updateLastWorkingDate(startDate: string): void {
    const calculatedLastDate = this.calculateLastWorkingDate(startDate);
    this.vacationApplyForm.patchValue({ lastWorkingDate: calculatedLastDate });
  }

  updateDueBackDate(lastDate: string): void {
    const calculatedBackDate = this.calculateBackDate(lastDate);
    this.vacationApplyForm.patchValue({ dueBackDate: calculatedBackDate });
  }

  calculateEndDate(startDate: string, duration: number): Date {
    const start = new Date(startDate);
    const endDate = new Date(start);
    endDate.setDate(start.getDate() + duration - 1);
    return endDate;
  }

  calculateLastWorkingDate(startDate: string): Date {
    const start = new Date(startDate);
    const endDate = new Date(start);
    endDate.setDate(start.getDate() - 1);
    return endDate;
  }

  calculateBackDate(endDate: string): Date {
    const backDate = new Date(endDate);
    const end = new Date(backDate);
    backDate.setDate(end.getDate() + 1);
    return backDate;
  }

  dateFilter = (d: Date | null): boolean => {
    this.fetchHolidays();
    const currentDate = d || new Date();

    if (!this.holidays || !Array.isArray(this.holidays)) {
      return true;
    }
    const isHoliday = this.holidays.some(
      (holidayDate: Date) => holidayDate.getTime() === currentDate.getTime()
    );

    return !isHoliday;
  };

  onApplyVacation() {
    this.isLoading = true;
    if (this.vacationApplyForm.invalid) {
      console.log(
        'this.vacationApplyForm.invalid',
        this.vacationApplyForm.controls
      );
      this.vacationApplyForm.markAllAsTouched();
      this.isLoading = false;
      return;
    }
    this.vacationApplyForm.get('lastWorkingDate')?.enable();
    this.vacationApplyForm.get('dueBackDate')?.enable();

    console.log('this.vacationApplyForm', this.vacationApplyForm);

    const today = new Date();
    const startDate = this.datePipe.transform(
      this.vacationApplyForm.value.startDate,
      'yyyy-MM-dd'
    );
    const lastWorkingDate = this.datePipe.transform(
      this.vacationApplyForm.value.lastWorkingDate,
      'yyyy-MM-dd'
    );
    const endDate = this.datePipe.transform(
      this.vacationApplyForm.getRawValue().endDate,
      'yyyy-MM-dd'
    );
    const formValue = this.vacationApplyForm.value;

    const isDateValid =
      startDate && endDate
        ? new Date(endDate) >= new Date(startDate)
          ? true
          : this.toastr.error(
              'To date cannot be earlier than from date',
              'Error'
            )
        : this.toastr.error('Choose a valid date', 'Error');

    const isLastDateValid =
      startDate && lastWorkingDate
        ? lastWorkingDate <= startDate
          ? true
          : this.toastr.error(
              'The last working date cannot be later than the from date.',
              'Error'
            )
        : this.toastr.error('Choose a valid date', 'Error');

    if (isDateValid === true && isLastDateValid === true) {
      const patchData = {
        startDate,
        endDate,
        travelTo: formValue?.travelTo,
        travelFrom: formValue?.travelFrom,
        isTicketEligible: formValue?.isTicketEligible,
        requestDate: this.datePipe.transform(today, 'yyyy-MM-dd'),
        lastWorkDate: this.datePipe.transform(
          formValue?.lastWorkingDate,
          'yyyy-MM-dd'
        ),
        dueBackDate: this.datePipe.transform(
          formValue?.dueBackDate,
          'yyyy-MM-dd'
        ),
        travelDate: this.datePipe.transform(
          formValue?.travelDate,
          'yyyy-MM-dd'
        ),
        employeeId: this.employeeId
          ? Number(this.employeeId)
          : Number(formValue?.employeeId),
        vacationStatus: 'Upcoming',
        status: 'Pending',
        isSpouseTicket: formValue?.isSpouseTicket,
        travelDateOfSpouse: this.datePipe.transform(
          formValue?.travelDateOfSpouse,
          'yyyy-MM-dd'
        ),
        travelToOfSpouse: formValue?.travelToOfSpouse,
        travelFromOfSpouse: formValue?.travelFromOfSpouse,
      };

      this.vacationApplyForm.patchValue(patchData);

      const vacationData = {
        ...patchData,
        note: formValue.notes,
      };
      if (vacationData) {
        this.vacationService.createVacationRequest(vacationData).subscribe(
          (response: any) => {
            console.log(response);

            if (response && !response.errors != null) {
              
              this.toastr.success('Vacation applied successfully!', 'Success');
              this.vacationApplyForm.reset();
              if (this.employeeId) {
                this.cancelEvent.emit();
              } else {
                this.router.navigate(['/vacation-management']);
              }
            } else if (response?.errors) {
              this.isLoading = false;
              this.toastr.error(response?.errors[0]?.message, 'Error');
            }
          },
          (error: Error) => {
            this.isLoading = false;
            this.toastr.error(error?.message, 'Error');
          }
        );
      }
    }
  }

  onCancel() {
    this.cancelEvent.emit();
  }
}
